/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger
} from '~/components/UI/shadcn/sheet'

import SearchIcon from '../../icons/search-icon'
import MenuIcon from '../../icons/menu-icon'
import { ChevronRight } from 'lucide-react'
import { cn } from '~/lib/utils'
import { Link } from 'react-router'
import MarkDown from '~/components/Helpers/MarkDown'
import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'
import ArrowRight from '~/components/UI/Buttons/icon/arrow-right'
import Search from '../Items/Search'
import Promo from '../Items/Promo'

const getBgColour = (colour: string) => {
  const colorMap: Record<string, string> = {
    'ink-blue': 'bg-blue-d text-white',
    'mid-blue': 'bg-blue-m text-white',
    'light-blue': 'bg-blue-l text-blue-d',
    chartreuse: 'bg-char text-blue-d',
    'light-pink': 'bg-pink text-blue-d',
    'light-aqua': 'bg-green text-blue-d',
    'light-purple': 'bg-purple text-blue-d',
    'light-orange': 'bg-orange text-blue-d',
    'light-grey': 'bg-grey-l text-blue-d',
    white: 'bg-white text-blue-d'
  }

  return colorMap[colour] || 'bg-blue-d text-white'
}

const MobileNav = ({ data, colour, ticker }: HeaderProps) => {
  const [menuState, setMenuState] = useState({
    isMenuOpen: false,
    isSearchOpen: false,
    activeItem: null as string | null,
    showSubMenu: false,
    isTransitioning: false
  })
  const [atTop, setAtTop] = useState(true)

  useEffect(() => {
    const handleScroll = () => setAtTop(window.scrollY === 0)
    window.addEventListener('scroll', handleScroll)
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const bgColour = getBgColour(colour ?? '')
  const isDarkBg = ['ink-blue', 'mid-blue'].includes(colour ?? '')
  const iconColour = isDarkBg ? 'white' : 'blue'
  const textColour = isDarkBg ? 'text-white' : 'text-blue-d'

  const activeItemData = useMemo(
    () =>
      Array.isArray(data)
        ? data.find(item => item.label === menuState.activeItem)
        : null,
    [data, menuState.activeItem]
  )

  const handleItemClick = (itemTitle: string) => {
    setMenuState(prev => ({
      ...prev,
      activeItem: prev.activeItem === itemTitle ? prev.activeItem : itemTitle,
      showSubMenu: prev.activeItem === itemTitle ? !prev.showSubMenu : true
    }))
  }

  const handleBackClick = () => {
    setMenuState(prev => ({
      ...prev,
      showSubMenu: false,
      activeItem: null
    }))
  }

  // Updated toggle handlers - key part of the fix
  const toggleNav = (type: 'menu' | 'search') => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (menuState.isTransitioning) return

    setMenuState(prev => ({ ...prev, isTransitioning: true }))

    const isTargetOpen =
      type === 'menu' ? menuState.isMenuOpen : menuState.isSearchOpen
    const isOtherOpen =
      type === 'menu' ? menuState.isSearchOpen : menuState.isMenuOpen

    if (isOtherOpen) {
      // Close the other menu first
      setMenuState(prev => ({
        ...prev,
        isMenuOpen: type === 'menu' ? false : false,
        isSearchOpen: type === 'search' ? false : false,
        showSubMenu: type === 'menu' ? false : prev.showSubMenu,
        activeItem: type === 'menu' ? null : prev.activeItem
      }))

      // Then open this menu after animation completes
      setTimeout(() => {
        setMenuState(prev => ({
          ...prev,
          isMenuOpen: type === 'menu' ? true : false,
          isSearchOpen: type === 'search' ? true : false
        }))

        setTimeout(() => {
          setMenuState(prev => ({ ...prev, isTransitioning: false }))
        }, 300)
      }, 200)
    } else if (isTargetOpen) {
      // Just close this menu
      setMenuState(prev => ({
        ...prev,
        isMenuOpen: type === 'menu' ? false : prev.isMenuOpen,
        isSearchOpen: type === 'search' ? false : prev.isSearchOpen,
        showSubMenu: type === 'menu' ? false : prev.showSubMenu,
        activeItem: type === 'menu' ? null : prev.activeItem
      }))

      setTimeout(() => {
        setMenuState(prev => ({ ...prev, isTransitioning: false }))
      }, 300)
    } else {
      // Open this menu
      setMenuState(prev => ({
        ...prev,
        isMenuOpen: type === 'menu' ? true : prev.isMenuOpen,
        isSearchOpen: type === 'search' ? true : prev.isSearchOpen
      }))

      setTimeout(() => {
        setMenuState(prev => ({ ...prev, isTransitioning: false }))
      }, 300)
    }
  }

  return (
    <div className='space-x-2xs'>
      <Sheet open={menuState.isSearchOpen}>
        <SheetTrigger asChild>
          <button
            className='pointer-events-auto z-50 ml-2'
            onClick={toggleNav('search')}
            disabled={menuState.isTransitioning}
          >
            <SearchIcon
              fill={iconColour}
              secondaryfill={''}
              title={'Search'}
              width={24}
              height={24}
              isActive={menuState.isSearchOpen}
              activeColour={colour}
            />
            <SheetTitle className='sr-only'>Search</SheetTitle>
          </button>
        </SheetTrigger>

        <SheetContent
          side='left'
          className={cn(
            // bgColour,
            'text-blue-d bg-white',
            'top-auto h-[calc(100dvh-70px)] w-full p-0 sm:h-[calc(100dvh-90px)] sm:max-w-none',
            ticker && atTop
              ? 'h-[calc(100dvh-100px)] sm:h-[calc(100dvh-122px)]'
              : ''
          )}
        >
          <div className='p-s sm:p-m space-y-m flex h-full flex-col'>
            <Search />
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={menuState.isMenuOpen}>
        <SheetTrigger asChild>
          <button
            className='pointer-events-auto z-50'
            onClick={toggleNav('menu')}
            disabled={menuState.isTransitioning}
          >
            <MenuIcon
              fill={iconColour}
              secondaryfill={''}
              title={'Main menu'}
              width={24}
              height={24}
              isActive={menuState.isMenuOpen}
              activeColour={colour}
            />
            <SheetTitle className='sr-only'>Toggle menu</SheetTitle>
          </button>
        </SheetTrigger>
        <SheetContent
          side='left'
          className={cn(
            bgColour,
            'pl-3xs md:pl-m top-auto h-[calc(100dvh-70px)] w-full p-0 sm:h-[calc(100dvh-90px)] sm:max-w-none',
            ticker && atTop
              ? 'h-[calc(100dvh-100px)] sm:h-[calc(100dvh-122px)]'
              : ''
          )}
        >
          <div className='flex h-full w-screen flex-col'>
            <div
              className={cn(
                textColour,
                'py-xs-plus space-x-2xs inline-flex items-center text-sm sm:hidden',
                'transition duration-300 ease-in-out',
                menuState.showSubMenu
                  ? 'translate-x-xs opacity-100'
                  : 'translate-x-0 opacity-0'
              )}
            >
              <button
                onClick={handleBackClick}
                className='focus:ring-primary focus:ring-offset font-medium hover:underline focus:ring focus:outline-none'
              >
                Main menu
              </button>
              {menuState.activeItem && (
                <>
                  <ChevronRight className='size-xs' />
                  <span className={textColour}>{menuState.activeItem}</span>
                </>
              )}
            </div>

            <div className='overflow-x-hidden overflow-y-auto'>
              <nav className='flex h-full'>
                <div
                  className={cn(
                    textColour,
                    'sm:pt-l w-full flex-none transition-all duration-300 ease-in-out',
                    menuState.showSubMenu
                      ? 'hidden sm:block sm:w-1/2'
                      : 'block w-full'
                  )}
                >
                  {Array.isArray(data) &&
                    data.map((item, index) => (
                      <div key={index} className=''>
                        {!item.link && (
                          <button
                            onClick={() => handleItemClick(item.label)}
                            className={cn(
                              menuState.showSubMenu &&
                                menuState.activeItem !== item.label
                                ? 'opacity-50 hover:opacity-75'
                                : 'opacity-100',
                              'p-xs group gap-2xs sm:gap-xs flex w-fit cursor-pointer items-center justify-between text-2xl font-bold transition-opacity duration-300 ease-in-out sm:text-3xl'
                            )}
                          >
                            {item.label}
                            <span>
                              <ChevronRight className='size-m group-hover:translate-x-2xs transition duration-300 ease-in-out' />
                            </span>
                          </button>
                        )}
                        {item.link && (
                          <Link
                            to={item.link.sys.uri}
                            className={cn(
                              menuState.showSubMenu &&
                                'opacity-50 hover:opacity-75',
                              'unstyled-link group p-xs gap-2xs sm:gap-xs flex w-fit items-center justify-between text-2xl font-bold no-underline transition-opacity duration-300 ease-in-out hover:text-current sm:text-3xl'
                            )}
                            reloadDocument
                          >
                            {item.label}
                            <span>
                              <ChevronRight className='size-m group-hover:translate-x-2xs transition duration-300 ease-in-out' />
                            </span>
                          </Link>
                        )}
                      </div>
                    ))}
                </div>

                <div
                  className={cn(
                    'text-blue-d flex h-[calc(100dvh)] w-full shrink-0 flex-col justify-between overflow-y-auto bg-white sm:h-[calc(100dvh-90px)] sm:w-1/2',
                    'absolute inset-x-0 sm:static sm:bottom-auto',
                    'transition-all duration-300 ease-in-out',
                    menuState.showSubMenu
                      ? ticker && atTop
                        ? 'space-y-m h-[calc(100dvh-160px)] grow translate-y-0'
                        : 'space-y-m h-[calc(100dvh-130px)] grow translate-y-0'
                      : 'translate-y-full sm:translate-y-0'
                  )}
                >
                  <div
                    className={cn(
                      menuState.showSubMenu ? 'space-y-m py-s' : '',
                      'sm:space-y-m px-2xs md:pr-2xl transition-opacity duration-300 ease-in-out sm:py-[36px] sm:pl-[36px]'
                    )}
                  >
                    <FeaturedLinks
                      data={data}
                      activeItem={menuState.activeItem}
                    />

                    <NormalLinks
                      data={data || []}
                      activeItem={menuState.activeItem}
                    />
                  </div>

                  {activeItemData?.promo.length > 0 && (
                    <Promo
                      title={activeItemData?.promo[0]?.title}
                      description={activeItemData?.promo[0]?.description}
                      // img={activeItemData?.promo?.img}
                      link={activeItemData?.promo[0]?.link}
                    />
                  )}
                </div>
              </nav>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

const FeaturedLinks = ({
  data,
  activeItem
}: {
  data: any
  activeItem: string | null
}) => (
  <div role='list' className='space-y-2xs list-none pl-0'>
    {Array.isArray(data) &&
      data
        .find(item => item.label === activeItem)
        ?.featured?.map((feat: any, index: number) => (
          <div role='listitem' key={feat.label + index}>
            <Link
              to={feat.link?.sys?.uri}
              className={cn(
                'unstyled-link hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground group space-y-2xs-plus p-2xs-plus text-blue-d hover:bg-grey-l dark:hover:bg-grey-d block rounded-none leading-none no-underline outline-hidden transition-colors select-none'
              )}
              reloadDocument
            >
              <div className='gap-2xs border-blue-d-40 stroke-blue-d pb-3xs text-blue-d group-hover:gap-xs group-focus:gap-xs relative z-0 flex w-fit items-center border-b-[1px] leading-none font-bold text-balance transition-all duration-200 group-hover:mr-0 group-focus:mr-0 dark:border-white/60 dark:stroke-white'>
                {feat.label}
                <ArrowRight
                  fill={''}
                  width={'1.437rem'}
                  height={'1.437rem'}
                  title={''}
                />
                <svg
                  height='2'
                  width='100%'
                  className='absolute -bottom-[1px] left-0 z-10 origin-left scale-x-0 transition-transform duration-200 group-hover:scale-x-100 group-focus:scale-x-100'
                >
                  <rect
                    x='0'
                    y={2}
                    width='100%'
                    height='1'
                    className='fill-blue-d dark:fill-white'
                    strokeWidth='2'
                    strokeLinecap='square'
                  />
                </svg>
              </div>
              {feat.description && (
                <span className='text-muted-foreground text-blue-d/80 dark:text-blue-d/80 line-clamp-2 text-base leading-snug'>
                  <MarkDown content={feat.description} />
                </span>
              )}
            </Link>
          </div>
        ))}
  </div>
)

const NormalLinks = ({
  data,
  activeItem
}: {
  data: any
  activeItem: string | null
}) => (
  <ul className='space-y-m pl-2xs-plus sm:pl-xs list-none'>
    {Array.isArray(data) &&
      data
        .find(item => item.label === activeItem)
        ?.normal?.map((normal: any, ind: number) => (
          <li className='w-fit' key={ind}>
            <TertiaryLink
              data={{
                strongColour: false,
                externalLink: normal.externalLink,
                internalLink: normal.entry
                  ? {
                      sys: {
                        uri:
                          normal.entry?.sys?.uri +
                          (normal.query ? normal.query : '')
                      }
                    }
                  : undefined,
                ariaLabel: normal.ariaLabel,
                newWindow: normal.externalLink || normal.target,
                label: normal.label
              }}
              colour={normal.colour}
            />
          </li>
        ))}
  </ul>
)

export default MobileNav
